/**
 * HMC Events Store
 *
 * Zustand store for HMC Events page state management.
 * Handles upload history, report events, file uploads, and re-enrichment.
 *
 * Updated: Uses SSE streaming for re-enrichment with real-time progress tracking.
 */

import { create } from 'zustand';
import { apiClient } from '@shared/services/apiClient';
import { useEnrichmentProgressStore } from '@shared/store/enrichmentProgressStore';
import { useNotificationStore } from '@shared/store/notificationStore';
import { logger } from '@shared/utils/logger';

// Music genres for concerts
export type MusicGenre = 'rap' | 'rnb' | 'latino' | 'rock' | 'pop' | 'country' | 'electronic' | 'other';

// Types
export interface HMCEvent {
  id: number;
  event_type: 'dynamo' | 'dash' | 'general';
  event_date: string;
  kickoff_time: string | null;
  opponent: string | null;
  venue: string | null;
  broadcast: string | null;
  score: string | null;
  special_event: string | null;
  is_home: boolean;
  event_url: string | null;
  youtube_url: string | null;
  enrichment_status: 'pending' | 'enriched' | 'failed' | 'skipped' | null;
  enriched_at: string | null;
  source_file: string | null;
  source_email_id: string | null;
  created_at: string;
  updated_at: string;
  // New fields for event details
  event_description: string | null;
  event_start_time: string | null;
  music_genre: MusicGenre | null;
  // Door opening time (from Toyota Center scraping, or calculated for Dynamo/Dash)
  doors_open: string | null;
  // Event image URL (from Toyota Center scraping)
  event_image_url: string | null;
  // Family-friendly flag (determined by OpenAI 4o during enrichment)
  is_family_friendly: boolean | null;
  // Total available tickets/seats
  total_available: number | null;
  // Availability flag - false for PC events or events with person names instead of teams
  is_available: boolean;
}

export interface UploadHistory {
  id: number;
  upload_id: string;
  filename: string;
  file_size: number;
  events_created: number;
  events_updated: number;
  enrichment_status: 'pending' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
  processed_at: string | null;
}

export interface UploadResult {
  success: boolean;
  upload_id: string;
  filename: string;
  events_created: number;
  events_updated: number;
  events_found: number;
  enrichment_status: string;
  message: string;
  new_event_ids?: number[];  // IDs of newly created events for notifications
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

// Ticket request backup selection state
export interface BackupSelection {
  primaryEventId: number | null;
  secondChoiceEventId: number | null;
  thirdChoiceEventId: number | null;
  isMinimized: boolean;
  /** Position where the modal was when minimized */
  minimizedPosition: { x: number; y: number } | null;
  formData: {
    fullName: string;
    company: string;
    title: string;
    email: string;
    phone: string;
    ticketCount: number;
    guestNames: string[];
    specialRequest: boolean;
    specialRequestNote: string;
  } | null;
}

// Event update payload for inline editing
export interface EventUpdatePayload {
  opponent?: string | null;
  event_start_time?: string | null;
  event_type?: 'dynamo' | 'dash' | 'general' | 'rockets' | 'concert' | 'comedy' | 'performance' | 'other';
  music_genre?: MusicGenre | null;
  youtube_url?: string | null;
  event_image_url?: string | null;
  event_date?: string | null;
}

// Extended event type for UI purposes (includes additional types)
export type EventTypeExtended = 'dynamo' | 'dash' | 'general' | 'rockets' | 'concert' | 'comedy' | 'performance' | 'other';

// Gmail poller status from /poll-gmail/status endpoint
export interface PollerStatus {
  is_polling: boolean;
  next_run: string | null;
  schedule: string;
  sync_state: {
    last_sync_time: string | null;
    total_emails_processed: number;
    total_events_created: number;
  } | null;
}

interface HMCEventsState {
  // Data
  events: HMCEvent[];
  uploadHistory: UploadHistory[];
  currentReport: UploadHistory | null;

  // Loading/error states
  loading: boolean;
  error: string | null;

  // Upload states
  uploadState: UploadState;
  uploadError: string | null;
  uploadResult: UploadResult | null;

  // Gmail poller status
  pollerStatus: PollerStatus | null;

  // Filters
  searchQuery: string;
  eventTypeFilter: 'all' | 'dynamo' | 'dash' | 'rockets' | 'concert' | 'non_sporting' | 'general';
  viewMode: 'grid' | 'list' | 'table';

  // Selection state
  selectedEventIds: Set<number>;

  // Backup selection state (for ticket requests)
  backupSelection: BackupSelection;

  // Actions
  fetchUploadHistory: () => Promise<void>;
  fetchReportEvents: (uploadId: string) => Promise<void>;
  fetchPollerStatus: () => Promise<void>;
  uploadFile: (file: File, eventType?: string) => Promise<void>;
  deleteReport: (uploadId: string) => Promise<void>;
  enrichSelectedEvents: (uploadId: string) => Promise<void>;
  updateEvent: (eventId: number, updates: EventUpdatePayload) => Promise<void>;

  // Filter actions
  setSearchQuery: (query: string) => void;
  setEventTypeFilter: (filter: 'all' | 'dynamo' | 'dash' | 'rockets' | 'concert' | 'non_sporting' | 'general') => void;
  setViewMode: (mode: 'grid' | 'list' | 'table') => void;

  // Selection actions
  toggleEventSelection: (eventId: number) => void;
  selectAllEvents: () => void;
  deselectAllEvents: () => void;
  selectFilteredEvents: (eventIds: number[]) => void;

  // Backup selection actions
  startBackupSelection: (primaryEventId: number, formData: BackupSelection['formData']) => void;
  setSecondChoice: (eventId: number | null) => void;
  setThirdChoice: (eventId: number | null) => void;
  minimizeBackupSelection: (position?: { x: number; y: number }) => void;
  restoreBackupSelection: () => void;
  clearBackupSelection: () => void;
  submitBackupSelections: () => Promise<void>;

  // State management
  resetUploadState: () => void;
  clearError: () => void;
}

export const useHMCEventsStore = create<HMCEventsState>((set, get) => ({
  // Initial state
  events: [],
  uploadHistory: [],
  currentReport: null,
  loading: false,
  error: null,
  uploadState: 'idle',
  uploadError: null,
  uploadResult: null,
  searchQuery: '',
  eventTypeFilter: 'all',
  viewMode: 'grid',
  selectedEventIds: new Set<number>(),
  backupSelection: {
    primaryEventId: null,
    secondChoiceEventId: null,
    thirdChoiceEventId: null,
    isMinimized: false,
    minimizedPosition: null,
    formData: null,
  },
  pollerStatus: null,

  // Fetch Gmail poller status
  fetchPollerStatus: async () => {
    try {
      const response = await apiClient.get<PollerStatus>('/houston-calendar/poll-gmail/status');
      set({ pollerStatus: response.data });
    } catch (error) {
      // Silently fail - poller status is not critical
      logger.error('[HMCEventsStore] Failed to fetch poller status:', error);
      set({ pollerStatus: null });
    }
  },

  // Fetch upload history
  fetchUploadHistory: async () => {
    set({ loading: true, error: null });

    try {
      const response = await apiClient.get<{
        uploads: UploadHistory[];
        total: number;
        limit: number;
        offset: number;
      }>('/houston-calendar/uploads?limit=50&offset=0');

      set({
        uploadHistory: response.data.uploads,
        loading: false,
      });
    } catch (error) {
      console.error('[HMCEventsStore] Failed to fetch upload history:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch upload history',
        loading: false,
      });
    }
  },

  // Fetch events for a specific report
  fetchReportEvents: async (uploadId: string) => {
    set({ loading: true, error: null });

    try {
      // Find the report in history
      const report = get().uploadHistory.find((h) => h.upload_id === uploadId);

      // Use upload_id filter - backend now uses junction table that properly tracks
      // event-upload relationships even when source_email_id gets overwritten
      const response = await apiClient.get<{
        events: HMCEvent[];
        count: number;
      }>('/houston-calendar/events', {
        params: { upload_id: uploadId },
      });

      set({
        events: response.data.events,
        currentReport: report || null,
        loading: false,
      });
    } catch (error) {
      console.error('[HMCEventsStore] Failed to fetch report events:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch report events',
        loading: false,
      });
    }
  },

  // Upload file
  uploadFile: async (file: File, eventType?: string) => {
    set({ uploadState: 'uploading', uploadError: null, uploadResult: null });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const params = eventType ? `?event_type=${eventType}` : '';

      const response = await apiClient.post<UploadResult>(
        `/houston-calendar/upload${params}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const result = response.data;

      set({
        uploadState: 'success',
        uploadResult: result,
      });

      // Emit notification for new events
      if (result.events_created > 0) {
        const notificationStore = useNotificationStore.getState();
        notificationStore.addNotification({
          type: 'hmc_new_events',
          title: 'New HMC Events Added',
          message: `${result.events_created} new event${result.events_created > 1 ? 's' : ''} added from "${result.filename}"${result.events_updated > 0 ? ` (${result.events_updated} updated)` : ''}`,
          severity: 'success',
          actionUrl: `/hmc-events?upload_id=${result.upload_id}`,
          actionLabel: 'View Events',
          metadata: {
            upload_id: result.upload_id,
            events_created: result.events_created,
            events_updated: result.events_updated,
            new_event_ids: result.new_event_ids,
          },
        });
      } else if (result.events_updated > 0) {
        // No new events, but some were updated (attendance sync)
        const notificationStore = useNotificationStore.getState();
        notificationStore.addNotification({
          type: 'hmc_upload_complete',
          title: 'HMC Events Updated',
          message: `${result.events_updated} event${result.events_updated > 1 ? 's' : ''} updated from "${result.filename}"`,
          severity: 'info',
          actionUrl: `/hmc-events?upload_id=${result.upload_id}`,
          actionLabel: 'View Events',
        });
      }

      // Refresh history
      get().fetchUploadHistory();
    } catch (error) {
      console.error('[HMCEventsStore] Upload failed:', error);
      set({
        uploadState: 'error',
        uploadError: error instanceof Error ? error.message : 'Upload failed',
      });
    }
  },

  // Delete report
  deleteReport: async (uploadId: string) => {
    set({ loading: true, error: null });

    try {
      await apiClient.delete(`/houston-calendar/uploads/${uploadId}`);

      // Remove from local state
      const { uploadHistory, currentReport } = get();
      const newHistory = uploadHistory.filter((h) => h.upload_id !== uploadId);

      set({
        uploadHistory: newHistory,
        // Clear current report if it was deleted
        currentReport: currentReport?.upload_id === uploadId ? null : currentReport,
        events: currentReport?.upload_id === uploadId ? [] : get().events,
        loading: false,
      });
    } catch (error) {
      console.error('[HMCEventsStore] Delete failed:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to delete report',
        loading: false,
      });
    }
  },

  /**
   * Enrich selected events using SSE streaming with real-time progress tracking.
   *
   * Uses the enrichmentProgressStore to show floating progress tracker that:
   * - Persists across page navigation
   * - Shows per-event progress (searching, found, failed, skipped)
   * - Supports cancellation
   * - Auto-dismisses after completion
   *
   * Only enriches events that are currently selected via checkboxes.
   */
  enrichSelectedEvents: async (uploadId: string) => {
    const progressStore = useEnrichmentProgressStore.getState();
    const { selectedEventIds } = get();

    // Get selected event IDs
    const eventIds = Array.from(selectedEventIds);
    const totalCount = eventIds.length;

    if (totalCount === 0) {
      set({ error: 'No events selected. Please select events to enrich.' });
      return;
    }

    // Start enrichment in progress store (shows floating tracker)
    progressStore.startEnrichment(uploadId, totalCount);

    try {
      // Get SSE token for authentication
      let token: string | null = null;
      const isDevSkipAuth = import.meta.env.DEV && import.meta.env.VITE_DEV_SKIP_AUTH === 'true';
      const devApiKey = import.meta.env.VITE_DEV_API_KEY || 'dev-key-12345';

      if (isDevSkipAuth) {
        token = `dev:${devApiKey}`;
        logger.log('[HMCEventsStore] Using dev API key for SSE');
      } else {
        // Get SSE token from backend (reads HttpOnly cookie)
        const response = await fetch('/api/auth/ws-token', {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          token = data.ws_token || null;
        }
      }

      if (!token) {
        throw new Error('Authentication required. Please log in to enrich events.');
      }

      // Build SSE URL with event_ids parameter
      const eventIdsParam = eventIds.join(',');
      const url = `/api/houston-calendar/uploads/${uploadId}/enrich/stream?token=${encodeURIComponent(token)}&event_ids=${encodeURIComponent(eventIdsParam)}`;

      logger.log('[HMCEventsStore] Connecting to SSE stream for', totalCount, 'selected events');

      // Connect to SSE stream
      const eventSource = new EventSource(url);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          logger.log('[HMCEventsStore] SSE event:', data.status);

          switch (data.status) {
            case 'started':
              // Update total count if different
              if (data.total !== totalCount) {
                progressStore.updateProgress(uploadId, { totalCount: data.total });
              }
              break;

            case 'progress':
              // Update current progress
              progressStore.updateProgress(uploadId, {
                currentIndex: data.current,
                enrichedCount: data.enriched_count || 0,
                failedCount: data.failed_count || 0,
              });

              // Update current event being processed
              progressStore.updateCurrentEvent(uploadId, {
                id: data.event_id,
                name: data.event_name || `Event ${data.event_id}`,
                phase: data.phase,
                eventUrl: data.event_url,
                youtubeUrl: data.youtube_url,
              });
              break;

            case 'completed':
              logger.log('[HMCEventsStore] Enrichment completed:', data);
              eventSource.close();

              progressStore.completeEnrichment(uploadId, {
                enriched: data.enriched_count,
                failed: data.failed_count,
              });

              // Clear selection and refresh data
              set({ selectedEventIds: new Set<number>() });
              get().fetchReportEvents(uploadId);
              get().fetchUploadHistory();
              break;

            case 'cancelled':
              logger.log('[HMCEventsStore] Enrichment cancelled:', data);
              eventSource.close();

              progressStore.cancelEnrichment(uploadId);

              // Refresh data to show partial results
              get().fetchReportEvents(uploadId);
              get().fetchUploadHistory();
              break;

            case 'error':
              logger.error('[HMCEventsStore] Enrichment error:', data.message);
              eventSource.close();

              progressStore.failEnrichment(uploadId, data.message);
              set({ error: data.message });
              break;
          }
        } catch (parseError) {
          logger.error('[HMCEventsStore] Failed to parse SSE event:', parseError);
        }
      };

      eventSource.onerror = (error) => {
        logger.error('[HMCEventsStore] SSE connection error:', error);
        eventSource.close();

        progressStore.failEnrichment(uploadId, 'Connection lost during enrichment');
        set({ error: 'Connection lost during enrichment. Please try again.' });
      };

    } catch (error) {
      logger.error('[HMCEventsStore] Enrichment failed:', error);

      progressStore.failEnrichment(
        uploadId,
        error instanceof Error ? error.message : 'Enrichment failed'
      );

      set({
        error: error instanceof Error ? error.message : 'Enrichment failed',
      });
    }
  },

  /**
   * Update a single event's properties (for inline editing in table view).
   * Updates both local state and persists to backend.
   */
  updateEvent: async (eventId: number, updates: EventUpdatePayload) => {
    const { events } = get();
    const eventIndex = events.findIndex((e) => e.id === eventId);

    if (eventIndex === -1) {
      logger.error('[HMCEventsStore] Event not found for update:', eventId);
      return;
    }

    // Optimistically update local state
    const originalEvent = events[eventIndex];
    const updatedEvent = { ...originalEvent, ...updates };
    const newEvents = [...events];
    newEvents[eventIndex] = updatedEvent;
    set({ events: newEvents });

    try {
      // Persist to backend
      await apiClient.patch(`/houston-calendar/events/${eventId}`, updates);
      logger.log('[HMCEventsStore] Event updated successfully:', eventId, updates);
    } catch (error) {
      // Rollback on error
      logger.error('[HMCEventsStore] Failed to update event:', error);
      const rollbackEvents = [...get().events];
      rollbackEvents[eventIndex] = originalEvent;

      // Extract error message from axios error or generic error
      let errorMessage = 'Failed to update event';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: string }; status?: number } };
        if (axiosError.response?.status === 409) {
          errorMessage = axiosError.response?.data?.detail || 'Event type conflict: another event with this type exists on this date';
        } else if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({
        events: rollbackEvents,
        error: errorMessage
      });
    }
  },

  // Filter actions
  setSearchQuery: (query) => set({ searchQuery: query }),
  setEventTypeFilter: (filter) => set({ eventTypeFilter: filter }),
  setViewMode: (mode) => set({ viewMode: mode }),

  // Selection actions
  toggleEventSelection: (eventId: number) => {
    const { selectedEventIds } = get();
    const newSet = new Set(selectedEventIds);
    if (newSet.has(eventId)) {
      newSet.delete(eventId);
    } else {
      newSet.add(eventId);
    }
    set({ selectedEventIds: newSet });
  },

  selectAllEvents: () => {
    const { events } = get();
    const allIds = new Set(events.map((e) => e.id));
    set({ selectedEventIds: allIds });
  },

  deselectAllEvents: () => {
    set({ selectedEventIds: new Set<number>() });
  },

  selectFilteredEvents: (eventIds: number[]) => {
    set({ selectedEventIds: new Set(eventIds) });
  },

  // Backup selection actions
  startBackupSelection: (primaryEventId, formData) => {
    set({
      backupSelection: {
        primaryEventId,
        secondChoiceEventId: null,
        thirdChoiceEventId: null,
        isMinimized: false,
        minimizedPosition: null,
        formData,
      },
    });
  },

  setSecondChoice: (eventId) => {
    const { backupSelection } = get();
    set({
      backupSelection: {
        ...backupSelection,
        secondChoiceEventId: eventId,
      },
    });
  },

  setThirdChoice: (eventId) => {
    const { backupSelection } = get();
    set({
      backupSelection: {
        ...backupSelection,
        thirdChoiceEventId: eventId,
      },
    });
  },

  minimizeBackupSelection: (position) => {
    const { backupSelection } = get();
    set({
      backupSelection: {
        ...backupSelection,
        isMinimized: true,
        minimizedPosition: position || null,
      },
    });
  },

  restoreBackupSelection: () => {
    const { backupSelection } = get();
    set({
      backupSelection: {
        ...backupSelection,
        isMinimized: false,
      },
    });
  },

  clearBackupSelection: () => {
    set({
      backupSelection: {
        primaryEventId: null,
        secondChoiceEventId: null,
        thirdChoiceEventId: null,
        isMinimized: false,
        minimizedPosition: null,
        formData: null,
      },
    });
  },

  submitBackupSelections: async () => {
    const { backupSelection, events } = get();

    if (!backupSelection.primaryEventId || !backupSelection.formData) {
      return;
    }

    // Get event details for submission
    const primaryEvent = events.find((e) => e.id === backupSelection.primaryEventId);
    const secondEvent = backupSelection.secondChoiceEventId
      ? events.find((e) => e.id === backupSelection.secondChoiceEventId)
      : null;
    const thirdEvent = backupSelection.thirdChoiceEventId
      ? events.find((e) => e.id === backupSelection.thirdChoiceEventId)
      : null;

    logger.log('[HMCEventsStore] Submitting backup selections:', {
      primary: primaryEvent?.opponent,
      second: secondEvent?.opponent,
      third: thirdEvent?.opponent,
      formData: backupSelection.formData,
    });

    // TODO: Send to backend when API is ready
    // For now, just clear the selection
    set({
      backupSelection: {
        primaryEventId: null,
        secondChoiceEventId: null,
        thirdChoiceEventId: null,
        isMinimized: false,
        minimizedPosition: null,
        formData: null,
      },
    });
  },

  // State management
  resetUploadState: () =>
    set({ uploadState: 'idle', uploadError: null, uploadResult: null }),
  clearError: () => set({ error: null }),
}));
